import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { MdAdd, MdPeople, MdGroups, MdCheckCircle } from "react-icons/md";
import Card from "components/card";
import Badge from "components/badge";
import Button from "components/button";
import StatCard from "components/stat-card";
import Skeleton from "components/skeleton/Skeleton";
import { EmptyState, ErrorState } from "components/states";
import { PageContainer, PageHeader } from "components/page";
import { SearchInput, Select } from "components/fields";
import groupsService from "services/groupsService";
import { getApiErrorMessage } from "api";
import { EMPTY, formatNumber, formatRelative } from "utils/format";
import { track } from "lib/mixpanel";
import { EVENTS } from "lib/events";
import { GROUP_TYPE_OPTIONS, GroupSwatch, groupTypeLabel } from "./groupDisplay";

// The list only shows people-management groups; heartbeat groups live under Heartbeat.
const GROUP_CATEGORY = "people_management";

const GroupCard = ({ group }) => (
  <Card
    as={Link}
    to={`/admin/groups/${group.id}`}
    padding="md"
    className="focus-ring gap-4 transition-colors hover:bg-surface-raised"
  >
    <div className="flex items-start gap-3">
      <GroupSwatch group={group} />
      <div className="min-w-0 flex-1">
        <h2 className="text-heading break-words">{group.name}</h2>
        <div className="mt-1 flex flex-wrap items-center gap-1.5">
          <Badge size="sm">{groupTypeLabel(group.groupType)}</Badge>
          {group.isSystem && (
            <Badge size="sm" tone="info">
              System
            </Badge>
          )}
          {!group.isActive && (
            <Badge size="sm" tone="danger" dot>
              Inactive
            </Badge>
          )}
        </div>
      </div>
    </div>

    {group.description && (
      <p className="line-clamp-2 text-sm text-fg-muted">{group.description}</p>
    )}

    <div className="mt-auto flex items-center justify-between gap-2 border-t border-line pt-3 text-sm text-fg-muted">
      <span className="flex items-center gap-1.5">
        <MdPeople aria-hidden="true" className="h-4 w-4" />
        {formatNumber(group.memberCount || 0)} {group.memberCount === 1 ? "member" : "members"}
      </span>
      {group.updatedAt && <span className="text-xs">Updated {formatRelative(group.updatedAt)}</span>}
    </div>
  </Card>
);

const GroupsList = () => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("");

  useEffect(() => {
    track(EVENTS.GROUPS_PAGE_VIEWED);
  }, []);

  const fetchGroups = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = { groupCategory: GROUP_CATEGORY };
      if (searchQuery) params.q = searchQuery;
      if (filterType) params.groupType = filterType;

      const response = await groupsService.getGroups(params);
      setGroups(response.success ? response.data : []);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, filterType]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  const totalMembers = groups.reduce((sum, group) => sum + (group.memberCount || 0), 0);
  const activeGroups = groups.filter((g) => g.isActive).length;
  const isFiltered = Boolean(searchQuery || filterType);

  const clearFilters = () => {
    setSearchQuery("");
    setFilterType("");
  };

  const createButton = (
    <Button as={Link} to="/admin/groups/new" leftIcon={<MdAdd className="h-5 w-5" />}>
      Create Group
    </Button>
  );

  let content;
  if (error) {
    content = (
      <ErrorState
        message={getApiErrorMessage(error, "We couldn't load your groups.")}
        onRetry={fetchGroups}
      />
    );
  } else if (loading) {
    content = (
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3" aria-busy="true">
        {Array.from({ length: 6 }, (_, i) => (
          <Skeleton key={i} className="h-48 w-full rounded-card" />
        ))}
      </div>
    );
  } else if (groups.length === 0) {
    content = isFiltered ? (
      <EmptyState
        variant="filtered"
        title="No groups match your filters"
        description="Try a different search or group type."
        action={
          <Button variant="secondary" onClick={clearFilters}>
            Clear Filters
          </Button>
        }
      />
    ) : (
      <EmptyState
        icon={MdGroups}
        title="No Groups Yet"
        description="Create your first group to organize people by ministry, team or life stage."
        action={createButton}
      />
    );
  } else {
    content = (
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
        {groups.map((group) => (
          <GroupCard key={group.id} group={group} />
        ))}
      </div>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title="Groups"
        description="Organize and manage your church groups"
        actions={createButton}
      />

      <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
        <StatCard
          icon={MdGroups}
          accent="sage"
          label="Total Groups"
          value={error ? EMPTY : formatNumber(groups.length)}
          loading={loading}
        />
        <StatCard
          icon={MdPeople}
          accent="sky"
          label="Total Members"
          value={error ? EMPTY : formatNumber(totalMembers)}
          loading={loading}
        />
        <StatCard
          icon={MdCheckCircle}
          accent="slate"
          label="Active Groups"
          value={error ? EMPTY : formatNumber(activeGroups)}
          loading={loading}
        />
      </div>

      <Card padding="sm" className="flex-col gap-3 md:flex-row md:items-center">
        <SearchInput
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Search groups by name or description…"
          ariaLabel="Search groups"
          className="flex-1"
        />
        <Select
          aria-label="Group type"
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="w-full md:w-56"
        >
          <option value="">All Types</option>
          {GROUP_TYPE_OPTIONS.map((type) => (
            <option key={type.value} value={type.value}>
              {type.label}
            </option>
          ))}
        </Select>
      </Card>

      {content}
    </PageContainer>
  );
};

export default GroupsList;
